/**
 * THE ONE MAPPING FROM A PROVISIONING STEP SLUG TO WORDS AN OPERATOR READS.
 *
 * Pure — no React, no fetching. Every surface that shows a provisioning step
 * goes through here, so the four of them cannot drift into four vocabularies.
 *
 * ─── WHY THIS FILE EXISTS ────────────────────────────────────────────────
 *
 * The backend emits SLUGS. `provisioning_lifecycle.count_steps` sets
 * `current_step` to `verification_steps[i]["step"]` verbatim, and four
 * surfaces rendered that value straight to the screen:
 *
 *     provisioningProgress.describeProgress   →  "18 of 20 steps · 90% ·
 *                                                 activity_tracking_defaults
 *                                                 · 59s elapsed"
 *     TenantDetailPage step table             →  <TableCell>{s.step}</TableCell>
 *     TenantDetailPage missing-steps list     →  <span>{m.step}</span>
 *     RepairProvisioningAction missing list   →  {String(step?.step ?? step)}
 *
 * The alternative that was NOT taken: phrasing each of those in its own JSX.
 * That is how a screen ends up saying "Setting up roles" in one place and
 * "Configuring permissions" in another for the same slug, and how a phrase
 * survives the deletion of the step it described. One module, four consumers.
 *
 * ─── WHERE THE SLUGS COME FROM, AND THE RULE ABOUT INVENTING THEM ────────
 *
 * Authority: `verify_instance()` in
 * `backend/shared/services/instance_provisioning.py` of sk-amroar/cluvik-platform.
 * `STAGE_ORDER` below is that function's `steps.append(...)` order, transcribed
 * — the same arrangement `identityTokens.js` uses for the Direction A ramp, and
 * for the same reason: two repositories, two builds, no shared package. The
 * honest options were a duplicated constant or a fabricated dependency, and a
 * duplicate that names its source and is checked against it is the smaller
 * cost. `backend/tests/test_provisioning_stage_labels.py` transcribes the list
 * a second time, independently, and fails on any divergence.
 *
 * NOTHING HERE IS INVENTED. In particular there is no `metadata` stage: that
 * word appears only in backend PROSE COMMENTS ("advancing from metadata to
 * layouts" in provisioning_lifecycle.py and provisioning_jobs_service.py) and
 * no code path has ever emitted it. A label map built from those comments
 * would put a stage on the screen that the product never reaches.
 *
 * ─── THE UNKNOWN SLUG IS THE INTERESTING CASE ────────────────────────────
 *
 * Admin and Platform deploy separately, so the backend can name a step this
 * build has never heard of. Three ways to handle that and only one is honest:
 *
 *   · render nothing — hides a step the server positively reported;
 *   · guess a phrase — states a meaning nobody defined;
 *   · show the slug, made readable, and say it is unrecognised.
 *
 * `describeStage` does the third. It never returns null for a non-empty slug,
 * and `known: false` lets a caller mark it without changing the words.
 */

/**
 * Every verification step slug the Platform can emit, in the order
 * `verify_instance` appends them. This ordering is load-bearing: `current_step`
 * is the FIRST required non-complete step in exactly this sequence, so the
 * position of a slug here is the position of the stage in the run.
 *
 * A non-CRM tenant emits a SUBSET — `emailmessage_layout`,
 * `contact_relationship_object` and `default_business_hours` are absent
 * entirely, and several others arrive `not_run`/`required: false`. The order of
 * what remains is unchanged, which is why one list covers both shapes.
 */
export const STAGE_ORDER = [
  'tenant_exists',
  'standard_objects',
  'standard_fields_baseline',
  'layouts',
  'emailmessage_layout',
  'contact_relationship_object',
  'crm_console_app',
  'home_page_defaults',
  'activity_tracking_defaults',
  'default_business_hours',
  'roles',
  'permission_sets',
  'tenant_settings',
  'entitlements',
  'system_list_views',
  'regional_defaults',
  'file_storage',
  'search_config',
  'page_assignments',
  'read_only_permission_set',
  'email_capability',
  'email_templates',
  'sample_data',
  'sample_connections',
];

/**
 * Slugs that reach a screen through `action_steps` but can NEVER be
 * `current_step`.
 *
 * The Provisioning tab's table concatenates `action_steps` with
 * `verification_steps`, so it renders both vocabularies in one column. They are
 * listed separately rather than merged into `STAGE_ORDER` because
 * `STAGE_ORDER` means "the run's sequence" and an action step has no position
 * in it — merging the two would make `stageIndex` lie.
 *
 * Note `regional_settings` (the action) beside `regional_defaults` (the
 * verification). They are two slugs, they are not a typo for one another, and
 * a map that folded them together would report the wrong one as reached.
 */
export const ACTION_STAGE_ORDER = [
  'base_objects',
  'standard_fields_apply',
  'sales_app',
  'contact_relationship',
  'history_tracking_defaults',
  'regional_settings',
  'permissions',
  'settings',
  // CRM Foundation decision 10: six starter reports and the Sales Overview
  // dashboard, provisioned for every CRM tenant whatever the sample choice.
  'starter_reports',
];

/**
 * Slug → the phrase an operator reads.
 *
 * WRITING RULE, and it is why these are not prettier. Each label names the
 * THING the step establishes, in the product's own nouns, and claims nothing
 * about what the step did to it. "Roles" — not "Setting up roles", not "Roles
 * configured". The same label has to sit in a progress sentence ("… · Roles ·
 * 59s elapsed"), in a table cell beside a status badge, and in a list of what
 * is MISSING; a verb that reads correctly in the first is a lie in the third.
 */
export const STAGE_LABELS = {
  // ── verification steps ──────────────────────────────────────────────
  tenant_exists: 'Tenant record',
  standard_objects: 'Standard objects',
  standard_fields_baseline: 'Standard field baseline',
  layouts: 'Page layouts',
  emailmessage_layout: 'Email message layout',
  contact_relationship_object: 'Contact relationships',
  crm_console_app: 'CRM Console app',
  home_page_defaults: 'Home page defaults',
  activity_tracking_defaults: 'Activity tracking defaults',
  default_business_hours: 'Business hours',
  roles: 'Roles',
  permission_sets: 'Permission sets',
  tenant_settings: 'Tenant settings',
  entitlements: 'Entitlements',
  system_list_views: 'System list views',
  regional_defaults: 'Regional defaults',
  file_storage: 'File storage',
  search_config: 'Search configuration',
  page_assignments: 'Page assignments',
  read_only_permission_set: 'Read-only permission set',
  email_capability: 'Email capability',
  email_templates: 'Email templates',
  sample_data: 'Sample records',
  sample_connections: 'Demo connections',

  // ── action steps ────────────────────────────────────────────────────
  base_objects: 'Base objects',
  standard_fields_apply: 'Standard fields',
  sales_app: 'Sales app',
  contact_relationship: 'Contact relationship',
  history_tracking_defaults: 'History tracking defaults',
  regional_settings: 'Regional settings',
  permissions: 'Permissions',
  settings: 'Settings',
  starter_reports: 'Starter reports',
};

/** Every slug this build can name, verification and action alike. */
export const KNOWN_STAGES = [...STAGE_ORDER, ...ACTION_STAGE_ORDER];

/** Does this build have a written label for `slug`? */
export const isKnownStage = (slug) =>
  Object.prototype.hasOwnProperty.call(STAGE_LABELS, String(slug || ''));

/**
 * Position in the run, or null.
 *
 * Null for an action step and for an unknown slug — both are "this has no place
 * in the verification sequence", and neither may be reported as position zero.
 */
export const stageIndex = (slug) => {
  const at = STAGE_ORDER.indexOf(String(slug || ''));
  return at === -1 ? null : at;
};

/**
 * A slug this build does not know, made readable — and no more than that.
 *
 * `foo_bar_baz` → `Foo bar baz`. Mechanical: underscores to spaces and one
 * capital. It deliberately does NOT consult a dictionary of near-misses or
 * expand abbreviations, because every such rule is a guess at a meaning the
 * server defined and this build did not receive.
 */
const humanise = (slug) => {
  const s = String(slug).replace(/[_-]+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

/**
 * The one description. `{ label, known, slug }`, or null for no slug at all.
 *
 * Null ONLY when there is no slug — `current_step` is legitimately null for a
 * READY tenant, for a FAILED job, and when nothing was measured, and those are
 * "no stage", not "an unnamed stage". Every other input, including a slug from
 * a newer Platform, gets words back.
 */
export const describeStage = (slug) => {
  const key = String(slug ?? '').trim();
  if (!key) return null;
  const label = STAGE_LABELS[key];
  return label
    ? { slug: key, label, known: true }
    : { slug: key, label: humanise(key), known: false };
};

/** Just the words, for a caller that has nowhere to put the `known` flag. */
export const stageLabel = (slug) => describeStage(slug)?.label ?? null;
